import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';

// Hero Component
function Hero() {
  return (
    <section className="hero" style={{
      position: 'relative',
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.7), rgba(30, 64, 175, 0.7)), url(/images/about_us.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      color: 'white',
      textAlign: 'center',
      padding: '2rem',
      marginBottom: '4rem',
      borderRadius: '0.5rem',
      overflow: 'hidden'
    }}>
      <div className="hero-content" style={{
        maxWidth: '800px',
        zIndex: 1,
        padding: '2rem',
        borderRadius: '0.5rem',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        backdropFilter: 'blur(5px)'
      }}>
        <h1 style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
        }}>Welcome to Cave Creek Labs</h1>
        <p style={{
          fontSize: '1.5rem',
          marginBottom: '2rem',
          textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)'
        }}>Innovative AI Solutions for a Smarter Tomorrow</p>
        <div className="cta-buttons">
          <a href="/apps" className="btn btn-primary" style={{
            backgroundColor: 'white',
            color: '#2563eb',
            border: '2px solid white',
            margin: '0 0.5rem 1rem',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            textDecoration: 'none',
            fontWeight: '600',
            display: 'inline-block',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>Explore Our Apps</a>
          <a href="/contact" className="btn btn-secondary" style={{
            backgroundColor: 'transparent',
            color: 'white',
            border: '2px solid white',
            margin: '0 0.5rem 1rem',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.375rem',
            textDecoration: 'none',
            fontWeight: '600',
            display: 'inline-block',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>Contact Us</a>
        </div>
      </div>
    </section>
  );
}

// Navigation Component
function Navigation() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="logo">Cave Creek Labs</Link>
        
        <div className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link>
          <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link>
          <Link to="/apps" className={location.pathname === '/apps' ? 'active' : ''}>Apps</Link>
          <Link to="/services" className={location.pathname === '/services' ? 'active' : ''}>Services</Link>
          <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''}>Contact</Link>
        </div>
        
        <button 
          className="mobile-menu-btn" 
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`hamburger ${menuOpen ? 'open' : ''}`}></span>
        </button>
      </div>
    </nav>
  );
}

// Page Components
function Home() {
  return (
    <>
      <Hero />
      <main className="container">
        <section className="features">
          <h2>Why Choose Us</h2>
          <div className="features-grid">
            <div className="feature-card">
              <h3>Cutting-Edge AI</h3>
              <p>Leveraging the latest in artificial intelligence to build powerful applications.</p>
            </div>
            <div className="feature-card">
              <h3>User-Focused</h3>
              <p>Designing with the end-user in mind for seamless experiences.</p>
            </div>
            <div className="feature-card">
              <h3>Innovative Solutions</h3>
              <p>Transforming complex problems into elegant solutions.</p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

function About() {
  return (
    <main className="container">
      <section className="about-section">
        <h1 className="text-center">About Cave Creek Labs</h1>
        <div className="about-content">
          <div className="about-text">
            <h2>Innovating the Future of AI</h2>
            <p>Founded in 2025, Cave Creek Labs is at the forefront of artificial intelligence innovation. Our team of expert developers, data scientists, and designers work together to create solutions that push the boundaries of what's possible with AI technology.</p>
            <p>We believe in creating technology that's not just powerful, but also accessible and intuitive for everyone. Our mission is to bridge the gap between cutting-edge AI research and practical, user-friendly applications.</p>
          </div>
          <div 
            className="about-image"
            style={{
              backgroundImage: 'url(/images/team_photo.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '0.5rem',
              minHeight: '400px',
              position: 'relative',
              overflow: 'hidden'
            }}
            aria-label="Our team at work"
          />
        </div>
        
        <div className="values-section">
          <h2>Our Core Values</h2>
          <div className="values-grid">
            <div className="value-card">
              <h3>Innovation</h3>
              <p>We constantly explore new technologies and approaches to solve complex problems in creative ways.</p>
            </div>
            <div className="value-card">
              <h3>Quality</h3>
              <p>We're committed to delivering products of the highest quality, with attention to every detail.</p>
            </div>
            <div className="value-card">
              <h3>User-First</h3>
              <p>Our users are at the heart of everything we do. We design with empathy and purpose.</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Apps() {
  const featuredApps = [
    {
      id: 1,
      title: "AI Assistant Pro",
      description: "Your personal AI assistant that helps with productivity, scheduling, and task management.",
      category: "Productivity",
      status: "Coming Soon",
      icon: "/images/apps/ai_assistant.png"
    },
    {
      id: 2,
      title: "Smart Photo Editor",
      description: "AI-powered photo editing that transforms your images with professional-quality enhancements.",
      category: "Photography",
      status: "Coming Soon",
      icon: "/images/apps/photo_editor.png"
    },
    {
      id: 3,
      title: "Language Translator",
      description: "Real-time translation for over 50 languages with natural-sounding voice synthesis.",
      category: "Communication",
      status: "Coming Soon",
      icon: "/images/apps/translator.png"
    }
  ];

  return (
    <main className="container">
      <section className="apps-section">
        <h1 className="text-center">Our Applications</h1>
        <p className="text-center mb-8">Discover our suite of AI-powered applications designed to enhance your digital experience.</p>
        
        <div className="apps-grid">
          {featuredApps.map((app) => (
            <div key={app.id} className="app-card">
              <div className="app-icon" style={{
                backgroundImage: `url(${app.icon})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100px',
                minHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <span style={{ display: 'none' }}>{app.title}</span>
              </div>
              <div className="app-details">
                <div className="app-header">
                  <h3>{app.title}</h3>
                  <span className={`status-badge ${app.status === 'Available Now' ? 'available' : 'coming-soon'}`}>
                    {app.status}
                  </span>
                </div>
                <p className="app-category">{app.category}</p>
                <p className="app-description">{app.description}</p>
                <button className="btn btn-secondary">
                  {app.status === 'Available Now' ? 'Download Now' : 'Learn More'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

function Services() {
  const services = [
    {
      title: "AI Development",
      description: "Custom AI solutions tailored to your business needs, from machine learning models to complete AI systems.",
      icon: "🤖"
    },
    {
      title: "Mobile App Development",
      description: "Beautiful, intuitive mobile applications for iOS and Android platforms.",
      icon: "📱"
    },
    {
      title: "Web Development",
      description: "Responsive, high-performance websites and web applications.",
      icon: "🌐"
    },
    {
      title: "UI/UX Design",
      description: "User-centered design that creates seamless and engaging experiences.",
      icon: "🎨"
    },
    {
      title: "AI Consulting",
      description: "Expert guidance on implementing AI solutions in your business.",
      icon: "💡"
    },
    {
      title: "Maintenance & Support",
      description: "Ongoing support and maintenance to keep your applications running smoothly.",
      icon: "🔧"
    }
  ];

  return (
    <main className="container">
      <section className="services-section">
        <h1 className="text-center">Our Services</h1>
        <p className="text-center mb-8">Comprehensive technology solutions to help your business grow in the digital age.</p>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card">
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <a href="/contact" className="learn-more">Learn More →</a>
            </div>
          ))}
        </div>
        
        <div className="cta-section text-center mt-12">
          <h2>Ready to Start Your Project?</h2>
          <p className="mb-6">Get in touch with our team to discuss how we can help bring your ideas to life.</p>
          <a href="/contact" className="btn btn-primary">Get Started</a>
        </div>
      </section>
    </main>
  );
}

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to a server
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', message: '' });
    
    // Reset form after 3 seconds
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <main className="container">
      <h1>Contact Us</h1>
      {submitted ? (
        <div className="success-message">
          <p>Thank you for your message! We'll get back to you soon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              required
            ></textarea>
          </div>
          <button type="submit" className="btn btn-primary">Send Message</button>
        </form>
      )}
      <div className="contact-info">
        <p>Or email us directly at: <a href="mailto:contact@numrico.com">contact@numrico.com</a></p>
      </div>
    </main>
  );
}

function App() {
  return (
    <Router>
      <div className="app">
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <footer className="footer">
          <div className="container">
            <p>&copy; {new Date().getFullYear()} Cave Creek Labs. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
